import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, KeyRound, Cake, Save } from 'lucide-react';
import { toast } from 'sonner';

/** iter77 — Profile self-management page (/profile). */
const ProfilePage = () => {
  const { api, user, setUser } = useAuth();
  const [profile, setProfile] = useState({
    full_name: user?.full_name || '',
    birthday: user?.birthday || '',
  });
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profile.full_name.trim()) { toast.error('Full name is required'); return; }
    setSavingProfile(true);
    try {
      const { data } = await api().put('/auth/profile', {
        full_name: profile.full_name.trim(),
        birthday: profile.birthday || '',  // iter112 — send empty string so backend clears the stored value
      });
      if (setUser) setUser(data);
      toast.success('Profile saved');
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Save failed');
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (pw.new_password !== pw.confirm) { toast.error('New passwords do not match'); return; }
    setSavingPw(true);
    try {
      await api().post('/auth/change-password', {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success('Password changed');
      setPw({ current_password: '', new_password: '', confirm: '' });
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Change failed');
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <User className="w-7 h-7 text-orange-500" /> My Profile
        </h1>
        <p className="text-slate-500 mt-1">Update your details and change your password.</p>
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <User className="w-4 h-4 text-blue-600" /> Personal details
          </h2>
        </CardHeader>
        <CardContent className="pt-5">
          <form onSubmit={saveProfile} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user?.email || ''} disabled className="rounded-sm mono bg-slate-50" data-testid="profile-email-input" />
              <p className="text-[11px] text-slate-500">Contact an admin if your email needs to change.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-full-name">Full name *</Label>
              <Input
                id="profile-full-name"
                value={profile.full_name}
                onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                className="rounded-sm"
                data-testid="profile-full-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-birthday" className="flex items-center gap-1.5"><Cake className="w-3.5 h-3.5" /> Birthday</Label>
              <Input
                id="profile-birthday"
                type="date"
                value={profile.birthday || ''}
                onChange={(e) => setProfile({ ...profile, birthday: e.target.value })}
                className="rounded-sm mono max-w-xs"
                data-testid="profile-birthday-input"
              />
              <p className="text-[11px] text-slate-500">We use this for birthday recognition on the TV kiosk.</p>
            </div>
            <Button type="submit" disabled={savingProfile} className="bg-orange-500 hover:bg-orange-600 rounded-sm" data-testid="profile-save-btn">
              <Save className="w-4 h-4 mr-1" /> {savingProfile ? 'Saving…' : 'Save profile'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-blue-600" /> Change password
          </h2>
        </CardHeader>
        <CardContent className="pt-5">
          <form onSubmit={changePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cp-current">Current password *</Label>
              <Input
                id="cp-current"
                type="password"
                value={pw.current_password}
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                required
                className="rounded-sm"
                data-testid="change-password-current-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-new">New password *</Label>
              <Input
                id="cp-new"
                type="password"
                value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                minLength={8}
                required
                className="rounded-sm"
                data-testid="change-password-new-input"
              />
              <p className="text-[11px] text-slate-500">At least 8 characters.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-confirm">Confirm new password *</Label>
              <Input
                id="cp-confirm"
                type="password"
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                minLength={8}
                required
                className="rounded-sm"
                data-testid="change-password-confirm-input"
              />
            </div>
            <Button type="submit" disabled={savingPw || !pw.current_password || !pw.new_password} className="bg-orange-500 hover:bg-orange-600 rounded-sm" data-testid="change-password-submit-btn">
              <KeyRound className="w-4 h-4 mr-1" /> {savingPw ? 'Changing…' : 'Change password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
